import { Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import moment from 'moment';

import { EntitySearchType } from '~/components/entity-search/entity-type';
import { EntityType } from '~/models/entities/entity-type';

export interface SupportDetailItem {
	label: string;
	displayValue: string;
	description?: string;
}

export class EntityTypeUtils {
	/**
	 * getUniqueID is utilized by React components and the local storage system to determine
	 * if two records are the same. (By building a string out of values unique to that record)
	 */
	public static getUniqueID(entity: EntitySearchType): string {
		if (!entity || !entity.values || !entity.entityType) {
			// If not set, make the key empty.
			return '';
		}
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				// NOTE: Jake: 2019-10-08 - WEBUI-2548
				// - MDz_Acct_No is retrived from getMember() only
				// - D2z_Client is retrieved from getSearchResults() only
				let clientNumber = entity.values.MDz_Acct_No
					? entity.values.MDz_Acct_No
					: entity.values.D2z_Client;
				// NOTE(Jae): 2020-02-26 - WEBUI-3103
				// Resolve issue where client number keys arent matching during EntitySearch.onSelectEntity
				// - "A10761_GSUP_1846055"
				// - "A10761_GSUP_      1846055"
				clientNumber = clientNumber.trim();
				return (
					entity.values.MDz_Member +
					'_' +
					entity.values.MDz_Fund +
					'_' +
					clientNumber
				);
			}

			case EntityType.EMPLOYER:
				return (
					entity.values.COz_Fund +
					'_' +
					entity.values.COz_Payroll +
					'_' +
					entity.values.COz_Payroll_Name
				);

			case EntityType.INVESTMENT_MANAGER:
				return entity.values.IMz_Code;

			case EntityType.ADVISER:
				return entity.values.BDz_Name;

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}
	}

	public static getDisplayName(entity: EntitySearchType): string {
		if (!entity || !entity.entityType) {
			return '';
		}
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				const record = entity.values;

				let displayName = '';
				if (record.D2z_Title) {
					displayName += record.D2z_Title.trim();
				}
				let surname = '';
				if (record.MDz_Surname) {
					surname = record.MDz_Surname;
				} else if (record.D2z_Surname) {
					surname = record.D2z_Surname;
				}
				displayName += ' ' + surname;
				let givenName = '';
				if (record.MDz_Given_Names) {
					givenName = record.MDz_Given_Names;
				} else if (record.D2z_Given_Names) {
					givenName = record.D2z_Given_Names;
				}
				if (surname && givenName) {
					displayName += ', ' + givenName;
				}
				return displayName.trim();
			}

			case EntityType.EMPLOYER: {
				if (entity.values.COz_Payroll_Name) {
					return entity.values.COz_Payroll_Name.trim();
				}
				break;
			}

			case EntityType.INVESTMENT_MANAGER: {
				return entity.values.IMz_Name;
			}

			case EntityType.ADVISER: {
				if (entity.values.BDz_Business_Name) {
					return entity.values.BDz_Business_Name;
				}
				return entity.values.BDz_Name;
			}

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}
		return '';
	}

	public static getMainDetails(entity: EntitySearchType): string[] {
		if (!entity || !entity.entityType) {
			return [];
		}
		const mainDetails = [];
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				const record = entity.values;
				if (record.MDz_Acct_No) {
					mainDetails.push(record.MDz_Acct_No.trim());
				} else if (record.D2z_Client) {
					mainDetails.push(record.D2z_Client.trim());
				}
				if (record.MDz_Fund) {
					// Division is related to FUND, put division information before the fund
					if (record.FRc_Division) {
						mainDetails.push(record.FRc_Division.trim());
					}
					mainDetails.push(record.MDz_Fund.trim());
				}
				if (record.MDz_Member) {
					mainDetails.push(record.MDz_Member.trim());
				}
				// NOTE: Jake: 2019-10-10 - WEBUI-2560
				// Our JavaDev DB will generally have this field
				// as an empty string for most records. However, this field
				// is utilized heavily by a client (see JIRA for whom), and so
				// as requested on the JIRA, I'll append this identifier only if its set.
				if (record.MDz_Cust_No) {
					mainDetails.push(record.MDz_Cust_No.trim());
				}
				break;
			}

			case EntityType.EMPLOYER:
				if (entity.values.COz_Payroll) {
					mainDetails.push(entity.values.COz_Payroll);
				}
				break;

			case EntityType.INVESTMENT_MANAGER:
				if (entity.values.IMz_Code) {
					mainDetails.push(entity.values.IMz_Code);
				}
				if (entity.values.IMz_Long_Code) {
					mainDetails.push(entity.values.IMz_Long_Code);
				}
				break;

			case EntityType.ADVISER:
				if (entity.values.BDz_Name) {
					mainDetails.push(entity.values.BDz_Name);
				}
				break;

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}
		return mainDetails;
	}

	public static getSupportingDetails(
		entity: EntitySearchType,
	): SupportDetailItem[] {
		if (!entity || !entity.entityType) {
			return [];
		}
		const i18n = Container.instance.get(I18N);
		switch (entity.entityType) {
			case EntityType.MEMBER: {
				const record = entity.values;
				let status = '';
				let statusDescription;
				if (record.MDz_Status) {
					status = record.MDz_Status.trim();
					if (entity.fieldModel && entity.fieldModel.MDz_Status) {
						statusDescription =
							entity.fieldModel.MDz_Status.fieldDescription;
					}
				}
				let category = '';
				let categoryDescription;
				if (record.MDz_Category) {
					category = record.MDz_Category.trim();
					if (record.CTz_Description) {
						categoryDescription = record.CTz_Description;
					}
				}
				let dateOfBirth = '';
				if (record.D2d_Birth) {
					dateOfBirth = moment(record.D2d_Birth.trim()).format('L');
				}
				const supportingDetails: SupportDetailItem[] = [
					{
						label: i18n.tr('entity-search.status'),
						displayValue: status,
						description: statusDescription,
					},
					{
						label: i18n.tr('entity-search.category'),
						displayValue: category,
						description: categoryDescription,
					},
				];
				supportingDetails.push({
					label: i18n.tr('entity-search.date-of-birth'),
					displayValue: dateOfBirth,
				});
				return supportingDetails;
			}

			case EntityType.EMPLOYER: {
				const record = entity.values;

				return [
					{
						label: i18n.tr('entity-search.fund'),
						displayValue: record.COz_Fund
							? record.COz_Fund.trim()
							: '',
					},
					{
						label: i18n.tr('entity-search.abn'),
						displayValue: record.COz_Aust_Company_No
							? record.COz_Aust_Company_No.trim()
							: '',
					},
				];
			}

			case EntityType.INVESTMENT_MANAGER: {
				const record = entity.values;
				const fieldModel = entity.fieldModel;

				const result: SupportDetailItem[] = [];
				if (record.IMz_Group) {
					let value = record.IMz_Group;
					if (
						fieldModel &&
						fieldModel.IMz_Group &&
						fieldModel.IMz_Group.fieldDescription
					) {
						value +=
							' (' + fieldModel.IMz_Group.fieldDescription + ')';
					}
					result.push({
						label: i18n.tr('entity-search.invest-group'),
						displayValue: value,
					});
				}
				if (record.IMz_AAS25_Grouping) {
					let value = record.IMz_AAS25_Grouping;
					if (
						fieldModel &&
						fieldModel.IMz_AAS25_Grouping &&
						fieldModel.IMz_AAS25_Grouping.fieldDescription
					) {
						value +=
							' (' +
							fieldModel.IMz_AAS25_Grouping.fieldDescription +
							')';
					}
					result.push({
						label: i18n.tr('entity-search.aas25-group'),
						displayValue: value,
					});
				}
				if (record.IMz_Status) {
					let value = record.IMz_Status;
					if (
						fieldModel &&
						fieldModel.IMz_Status &&
						fieldModel.IMz_Status.fieldDescription
					) {
						value +=
							' (' +
							entity.fieldModel.IMz_Status.fieldDescription +
							')';
					}
					result.push({
						label: i18n.tr('entity-search.status'),
						displayValue: value,
					});
				}

				return result;
			}

			case EntityType.ADVISER:
				// no-op
				break;

			default:
				const exhaustiveCheck: never = entity;
				throw new Error(
					'Unhandled entity type: ' + (entity as any).entityType,
				);
		}

		return [];
	}
}
